import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  Ship, 
  FileCheck, 
  Link2, 
  DollarSign, 
  Bell, 
  ChevronRight,
  Mail,
  FileX,
  Eye,
  AlertTriangle,
  LayoutDashboard,
  CheckSquare,
  Upload,
  BarChart3,
  Check
} from 'lucide-react';

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-importdocs-bg">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-importdocs-primary rounded-md flex items-center justify-center">
              <Ship className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading font-bold text-xl text-importdocs-text">ImportDocs</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/login">
              <Button variant="ghost" className="text-importdocs-text-secondary hover:text-importdocs-text" data-testid="nav-login-btn">
                Login
              </Button>
            </Link>
            <Link to="/signup">
              <Button className="bg-importdocs-primary hover:bg-importdocs-primary-dark text-white" data-testid="nav-signup-btn">
                Get Started Free
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          {/* https://images.unsplash.com/photo-1717940726344-05e5af5058da?w=1920&q=80 */}
          <img 
            src="https://images.unsplash.com/photo-1602066556450-62f988867a31?q=80&w=1032&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D" 
            
            alt="Port background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/80 to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="max-w-2xl">
            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-white leading-tight mb-6 animate-fade-in">
              Avoid customs delays caused by missing import documents
            </h1>
            <p className="text-lg text-slate-300 mb-8 leading-relaxed animate-fade-in stagger-1">
              See exactly which documents are missing for each shipment, collect them from suppliers, and stay ready for clearance.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in stagger-2">
              <Link to="/signup">
                <Button size="lg" className="bg-importdocs-secondary hover:bg-orange-600 text-white px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl" data-testid="hero-get-started-btn">
                  Get Started Free
                  <ChevronRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20 px-8 py-6 text-lg" data-testid="hero-login-btn">
                  Login
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-importdocs-secondary font-semibold text-sm uppercase tracking-wide">The Problem</span>
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-importdocs-text mt-3 mb-6">
                Import document chaos turns into customs delays
              </h2>
              <div className="space-y-5">
                {[
                  { icon: Mail, text: "Documents arrive late or across multiple emails" },
                  { icon: FileX, text: "You don’t know what’s missing until the broker asks" },
                  { icon: Eye, text: "No clear view of whether a shipment is ready for clearance" },
                  { icon: AlertTriangle, text: "Last-minute issues lead to delays, rework, and extra costs" },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4 animate-fade-in" style={{ animationDelay: `${i * 0.1}s` }}>
                    <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-red-600" />
                    </div>
                    <p className="text-importdocs-text-secondary text-lg pt-2">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              {/*src="https://images.unsplash.com/photo-1566699270403-3f7e3f340664?w=800&q=80" */}
              <img 
                src="https://plus.unsplash.com/premium_photo-1664475080491-023e76335e40?q=80&w=872&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                alt="Messy paperwork"
                className="rounded-lg shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-20 bg-importdocs-subtle">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <img 
                src="https://images.pexels.com/photos/4484078/pexels-photo-4484078.jpeg?w=800" 
                alt="Digital solution"
                className="rounded-lg shadow-xl"
              />
            </div>
            <div className="order-1 lg:order-2">
              <span className="text-importdocs-success font-semibold text-sm uppercase tracking-wide">The Solution</span>
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-importdocs-text mt-3 mb-6">
                ImportDocs helps you prepare shipments for clearance before they arrive.
              </h2>
              <div className="space-y-5">
                {[
                  { icon: LayoutDashboard, text: "A single dashboard showing each shipment’s clearance readiness status" },
                  { icon: CheckSquare, text: "A document checklist per shipment, so you know what’s missing" },
                  { icon: Upload, text: "Simple supplier upload links — no login required" },
                  { icon: Mail, text: "Automatic supplier follow-ups when documents are missing" },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4 animate-fade-in" style={{ animationDelay: `${i * 0.1}s` }}>
                    <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-emerald-600" />
                    </div>
                    <p className="text-importdocs-text-secondary text-lg pt-2">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-importdocs-primary font-semibold text-sm uppercase tracking-wide">Features</span>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-importdocs-text mt-3">
              Everything you need to get shipments ready for customs
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Ship, title: "Shipment Dashboard", desc: "See all shipments in one place — clearly marked as ready, missing documents, or at risk." },
              { icon: FileCheck, title: "Document Checklist", desc: "Track required documents per shipment with automatic completion tracking" },
              { icon: Link2, title: "Vendor Upload Portal", desc: "Share secure links with suppliers to upload documents directly" },
              { icon: DollarSign, title: "Cost Comparison", desc: "Compare costs early and spot differences before they hit your bottom line." },
              { icon: Bell, title: "Smart Automatic Follow-ups", desc: "When documents are missing, ImportDocs automatically notifies your supplier so you don’t have to chase." },
              { icon: CheckSquare, title: "Custom Documents", desc: "Add custom document types to your checklist as needed" },
            ].map((feature, i) => (
              <Card key={i} className="feature-card p-6 border border-slate-200 bg-white" data-testid={`feature-card-${i}`}>
                <div className="w-12 h-12 rounded-lg bg-importdocs-primary/10 flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-importdocs-primary" />
                </div>
                <h3 className="font-heading font-bold text-lg text-importdocs-text mb-2">{feature.title}</h3>
                <p className="text-importdocs-text-secondary">{feature.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-importdocs-subtle">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-importdocs-primary font-semibold text-sm uppercase tracking-wide">How It Works</span>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-importdocs-text mt-3">
              Simple 4-step process
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { step: "1", title: "Create a shipment", desc: "Add shipment details and cost estimates" },
              { step: "2", title: "Share upload link", desc: "Send secure link to your supplier" },
              { step: "3", title: "Track documents", desc: "Monitor uploads and cost variances" },
              { step: "4", title: "Clear with confidence", desc: "All documents ready for customs" },
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="w-16 h-16 rounded-full bg-importdocs-primary text-white font-heading font-bold text-2xl flex items-center justify-center mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="font-heading font-bold text-lg text-importdocs-text mb-2">{item.title}</h3>
                <p className="text-importdocs-text-secondary">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-importdocs-primary font-semibold text-sm uppercase tracking-wide">Pricing</span>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-importdocs-text mt-3">
              Simple, transparent pricing
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              { 
                name: "Free", 
                price: "$0", 
                period: "forever",
                features: ["Up to 3 shipments/year", "Document checklist", "Cost tracking", "Vendor upload links", "Custom documents"],
                cta: "Get Started",
                highlight: false,
                link: "/signup"
              },
              { 
                name: "Basic", 
                price: "$14.99", 
                period: "/month",
                features: ["Up to 20 shipments/year", "Document checklist", "Cost tracking", "Vendor upload links", "Custom documents"],
                cta: "Start Free Trial",
                highlight: true,
                link: "/comingsoon"
              },
              { 
                name: "Pro", 
                price: "$29.99", 
                period: "/month",
                features: ["Unlimited shipments", "Document checklist", "Cost tracking", "Vendor upload links", "Custom documents", "Smart follow-up"],
                cta: "Start Free Trial",
                highlight: false,
                link: "/comingsoon"
              },
            ].map((plan, i) => (
              <Card key={i} className={`p-8 ${plan.highlight ? 'pricing-highlight' : 'border border-slate-200'}`} data-testid={`pricing-${plan.name.toLowerCase()}`}>
                <h3 className="font-heading font-bold text-xl text-importdocs-text">{plan.name}</h3>
                <div className="mt-4 mb-6">
                  <span className="font-heading font-extrabold text-4xl text-importdocs-text">{plan.price}</span>
                  <span className="text-importdocs-text-muted">{plan.period}</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, j) => (
                    <li key={j} className="flex items-center gap-2 text-importdocs-text-secondary">
                      <Check className="w-5 h-5 text-importdocs-success" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link to={plan.link}>
                  <Button className={`w-full ${plan.highlight ? 'bg-importdocs-primary hover:bg-importdocs-primary-dark text-white' : 'bg-slate-100 hover:bg-slate-200 text-importdocs-text'}`}>
                    {plan.cta}
                  </Button>
                </Link>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-importdocs-primary">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-6">
            Be ready for customs before your shipment arrives
          </h2>
          <p className="text-slate-300 text-lg mb-8">
            Organise import documents, track readiness, and avoid last-minute surprises with ImportDocs.
          </p>
          <Link to="/signup">
            <Button size="lg" className="bg-importdocs-secondary hover:bg-orange-600 text-white px-8 py-6 text-lg font-semibold shadow-lg" data-testid="final-cta-btn">
              Create Free Account
              <ChevronRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-importdocs-primary-dark">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-white/20 rounded-md flex items-center justify-center">
                <Ship className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading font-bold text-xl text-white">ImportDocs</span>
            </div>
            <div className="flex items-center gap-8 text-slate-400">
              <Link to="/login" className="hover:text-white transition-colors">Login</Link>
              <Link to={"/Privacy"}><p className="hover:text-white transition-colors">Privacy Policy</p></Link>
              <Link to={"/Terms"}><p className="hover:text-white transition-colors">Terms of Service</p></Link>
            </div>
            <p className="text-slate-500 text-sm">© 2026 ImportDocs. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
